import { faQuoteLeft, faStar } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import React, { useEffect, useState } from "react"
import SbEditable from "storyblok-react"
import { m, useAnimation, Variants } from "framer-motion"
import tw, { css } from "twin.macro"
import { Swiper, SwiperSlide } from "swiper/react"
import "swiper/css"
import "swiper/css/pagination"
import "swiper/css/navigation"
import { Autoplay, Pagination } from "swiper/modules"
import styled from "styled-components"
import { ReviewCarouselStoryblok, ReviewStoryblok } from "../../../component-types-sb"
import { useGlobalSettings } from "../../contexts/GlobalSettingsContext"
import Container from "../general/Container"
import Image from "../general/Image"
import { H4, P } from "../typography/Typography"
import InViewWrapper from "../general/InViewWrapper"
import ClientOnly from "../general/ClientOnly"


const Wrapper = styled(InViewWrapper)<{ background?: boolean }>(() => [
  tw`w-full max-w-[1200px] py-8 my-4
  [&[data-has-padding="false"]]:(
    py-0 my-0
  )`,
  ({ background }) => background && tw`bg-[#FFEDE6] rounded-2xl`,
])

const cardVariants: Variants = {
  onscreen: {
    transition: {
      staggerChildren: 0.4,
    },
  },
}

const Card = tw(m.div)`
  flex flex-col items-center
  relative
  bg-[#004E5E] text-white
  mb-8
  rounded-3xl
  max-w-[98%] w-full h-[400px]
  overflow-hidden
  shadow-md
  [[data-big-review="true"] &]:(
  justify-center
  )
`

const ReviewRating = tw.div`
  text-24 text-yellow
  mb-6
  [[data-big-review="false"] &]:(
  mt-8
  )
`

const QuoteIcon = tw.div`
  absolute left-1/2 -translate-x-1/2
  text-24
  top-9
  bg-[#00829C] text-white
  flex items-center justify-center
  z-10
  rounded-full
  w-[45px] h-[45px]
`

const textVariants: Variants = {
  offscreen: {
    opacity: 0,
    y: 50,
  },
  onscreen: {
    opacity: 1,
    transition: {
      duration: 0.5,
    },
    y: 0,
  },
}

const FramerH4 = m(H4)
const ReviewText = tw(FramerH4)`
  text-center
  justify-center
  my-0 mb-2
  px-12
  text-20 text-16
  desktop:[[data-big-review="true"] &]:(
    px-32
  )
  not-desktop:[[data-big-review="true"] &]:(
    px-16 text-20
  )
  [[data-big-review="true"] &]:(
    text-26 tablet:text-24
  )
`

const ReviewInfo = tw.div`
  flex flex-col self-center items-center
  px-16
  `

const ReviewAuthor = tw(P)`
  text-14 font-normal
  mb-0
  [[data-big-review="true"] &]:(
  text-18
  )
  not-desktop:[[data-big-review="true"] &]:(
  text-14 tablet:text-16
  )
`

const BadgeGrid = tw.div`
  grid grid-cols-2 tablet:grid-cols-4 gap-4 mt-8
  [img]:w-[110px]
`

const ReviewsContainer = tw(Container)`
  [[data-has-padding='false'] &]:(px-0 tablet:px-0)
`

const FullWidthSlider = tw.div`
  max-w-[90%]
`

const CustomPagination = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin-top: 1rem;

  .swiper-pagination-bullet {
    ${tw`w-6 h-6 bg-[#004E5E] opacity-100`}
  }

  .swiper-pagination-bullet-active {
    ${tw`bg-[#00829C]`}
  }
`

interface Props {
  readonly blok: ReviewCarouselStoryblok
}

const ReviewCarousel: React.FC<Props> = ({ blok }) => {
  const { badges } = useGlobalSettings()
  const controls = useAnimation()
  const [slidesPerView, setSlidesPerView] = useState(1)

  useEffect(() => {
    controls.start("onscreen").catch((error: unknown) => console.error(error))

    const updateSlidesPerView = () => {
      if (blok.big_review || blok.reviews.length === 1)
        setSlidesPerView(1)
       else if (blok.reviews.length === 2)
        setSlidesPerView(window.innerWidth >= 768 ? 2 : 1)
       else if (blok.reviews.length >= 3)
        if (window.innerWidth >= 1280)
          setSlidesPerView(3)
         else if (window.innerWidth >= 768)
          setSlidesPerView(2)
         else
          setSlidesPerView(1)
    }

    if (typeof window !== "undefined") {
      updateSlidesPerView()
      window.addEventListener("resize", updateSlidesPerView)
    }

    return () => {
      if (typeof window !== "undefined")
        window.removeEventListener("resize", updateSlidesPerView)

    }
  }, [controls, blok])

  const showPagination = blok.big_review ? blok.reviews.length > 2 : blok.reviews.length > 3

  return <SbEditable content={blok}>
    <ClientOnly>
      <Wrapper
        data-has-padding={blok.padding}
        data-big-review={blok.big_review}
        background={blok.background}
      >
        <ReviewsContainer>
          <FullWidthSlider>
            <Swiper
              spaceBetween={20}
              slidesPerView={slidesPerView}
              loop={blok.reviews.length > slidesPerView}
              autoplay={{
                delay: 7500,
                disableOnInteraction: false,
              }}
              pagination={showPagination ? {
                clickable: true,
                el: '.custom-pagination',
                enabled: true,
              } : false}
              modules={[Autoplay, Pagination]}
            >
              {blok.reviews.map((review, index) => {
                const { content: rawReview } = review as { content: ReviewStoryblok }
                if (!rawReview) return null
                return (
                  <SwiperSlide tw="justify-items-center" key={index}>
                    <div tw="relative flex flex-col items-center w-full pt-16">
                      <QuoteIcon>
                        <FontAwesomeIcon icon={faQuoteLeft} />
                      </QuoteIcon>
                      <Card variants={cardVariants} animate={controls}>
                        {blok.review_stars ? (
                        <ReviewRating>
                            {[...Array(5).keys()].map((i) => (
                              <FontAwesomeIcon key={i} icon={faStar} />
                            ))}
                        </ReviewRating>
                        ) : (!blok.big_review && <div tw="mt-[5.30rem]" />)
                        }
                        <ReviewText variants={textVariants} animate={controls}>
                          {rawReview.quote}
                        </ReviewText>
                        <ReviewInfo>
                          <ReviewAuthor tw="font-bold">{rawReview.quotee}</ReviewAuthor>
                          <ReviewAuthor tw="text-center">{rawReview.functionQuotee}</ReviewAuthor>
                          </ReviewInfo>
                      </Card>
                    </div>
                  </SwiperSlide>
                )
              })}
            </Swiper>
            {showPagination && <div className="custom-pagination" css={CustomPagination} />}
          </FullWidthSlider>
          {blok.show_badges && (
            <BadgeGrid>
              {badges.map((badge) => <Image key={badge._uid} image={badge.image} width="75" />)}
            </BadgeGrid>
          )}
        </ReviewsContainer>
      </Wrapper>
    </ClientOnly>
  </SbEditable>
}

export default ReviewCarousel
